import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container, Typography, Box, Paper, Table, TableHead, TableRow, TableCell, TableBody,
  Button, Chip, Stack, MenuItem, Select, Alert, Snackbar, CircularProgress, Divider, TextField,
} from '@mui/material';
import { getTenantDetail, setTenantStatus, impersonateTenant, setTenantAssessmentPrice } from '../../api/saasApi';
import { useAuth } from '../../context/AuthContext';

const STATUSES = ['Active', 'Suspended'];
const color = (s) => ({ Active: 'success', Suspended: 'error' }[s] || 'default');

const Fact = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
    <Typography variant="body2" fontWeight={600}>{value ?? '—'}</Typography>
  </Box>
);

/** Everything about one clinic: owner, members, roles, pricing and recent activity. */
const TenantDetail = () => {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');

  const [price, setPrice] = useState('');
  const [savingPrice, setSavingPrice] = useState(false);

  const load = async () => {
    try {
      const res = await getTenantDetail(tenantId);
      if (res.data?.succeeded === false) setError(res.data.messages?.[0] || 'Clinic not found.');
      else setData(res.data?.data || null);
    } catch (e) {
      setError('Failed to load clinic.');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [tenantId]);

  useEffect(() => {
    if (data?.assessmentPrice != null) setPrice(String(data.assessmentPrice));
  }, [data]);

  const changeStatus = async (status) => {
    await setTenantStatus(Number(tenantId), status);
    setToast('Status updated.');
    load();
  };

  const savePrice = async () => {
    setSavingPrice(true);
    try {
      const res = await setTenantAssessmentPrice(Number(tenantId), Number(price), data?.currency || 'INR');
      if (res.data?.succeeded === false) setToast(res.data.messages?.[0] || 'Could not save price.');
      else { setToast('Assessment price updated.'); load(); }
    } catch (e) {
      setToast('Could not save price.');
    } finally {
      setSavingPrice(false);
    }
  };

  const impersonate = async () => {
    const res = await impersonateTenant(Number(tenantId));
    const token = res.data?.data;
    if (token) {
      login(token, { impersonating: true, tenantId: Number(tenantId) });
      window.location.href = '/patient';
    }
  };

  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;
  if (error) return <Container sx={{ py: 4 }}><Alert severity="warning">{error}</Alert></Container>;

  const t = data?.tenant;
  const currency = data?.currency || 'INR';

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Button size="small" onClick={() => navigate('/platform/tenants')} sx={{ mb: 1 }}>← All clinics</Button>

      <Stack direction="row" spacing={1} alignItems="center" mb={2} flexWrap="wrap" useFlexGap>
        <Typography variant="h5" fontWeight={700}>{t?.name}</Typography>
        <Chip size="small" label={t?.status} color={color(t?.status)} />
        <Box flexGrow={1} />
        <Select size="small" value={STATUSES.includes(t?.status) ? t?.status : ''} onChange={(e) => changeStatus(e.target.value)} displayEmpty>
          {STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
        </Select>
        <Button size="small" variant="outlined" onClick={impersonate}>Impersonate</Button>
      </Stack>

      <Paper sx={{ p: 2.5, mb: 3 }}>
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' } }}>
          <Fact label="Slug" value={t?.slug} />
          <Fact label="Clinic code" value={data?.joinCode} />
          <Fact label="Owner" value={data?.ownerName ? `${data.ownerName} (${data.ownerEmail})` : 'not set'} />
          <Fact
            label="Assessment price"
            value={data?.assessmentPrice != null ? `${currency} ${data.assessmentPrice}` : 'platform default'}
          />
          <Fact label="Patients" value={data?.patientCount} />
          <Fact label="Assessments" value={data?.assessmentCount} />
        </Box>

        <Divider sx={{ my: 2 }} />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
          <Typography variant="body2" fontWeight={600}>Set per-assessment price</Typography>
          <TextField
            size="small"
            type="number"
            label={`Price (${currency})`}
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            sx={{ maxWidth: 200 }}
            inputProps={{ min: 0, step: '1' }}
          />
          <Button variant="contained" onClick={savePrice} disabled={savingPrice || price === ''}>
            {savingPrice ? 'Saving…' : 'Save price'}
          </Button>
        </Stack>
      </Paper>

      <Paper sx={{ mb: 3 }}>
        <Typography variant="h6" fontWeight={700} sx={{ p: 2, pb: 0 }}>
          Members ({data?.users?.length ?? 0})
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Membership</TableCell>
              <TableCell>Last login</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(data?.users || []).map((u) => (
              <TableRow key={u.id}>
                <TableCell>
                  {u.name}
                  {u.isOwner && <Chip size="small" label="owner" sx={{ ml: 1 }} />}
                </TableCell>
                <TableCell>{u.email}</TableCell>
                <TableCell>{u.roleName || '—'}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={u.membershipStatus}
                    color={u.membershipStatus === 'Active' ? 'success' : u.membershipStatus === 'Pending' ? 'warning' : 'default'}
                  />
                </TableCell>
                <TableCell>{u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleDateString() : '—'}</TableCell>
              </TableRow>
            ))}
            {(data?.users || []).length === 0 && (
              <TableRow><TableCell colSpan={5}>No members.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
        <Divider />
        <Box sx={{ p: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Roles: {(data?.roles || []).map((r) => r.name).join(', ') || '—'}
          </Typography>
        </Box>
      </Paper>

      <Paper>
        <Typography variant="h6" fontWeight={700} sx={{ p: 2, pb: 0 }}>Recent activity</Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>When</TableCell>
              <TableCell>Action</TableCell>
              <TableCell>Target</TableCell>
              <TableCell>Detail</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(data?.recentActivity || []).map((a) => (
              <TableRow key={a.id}>
                <TableCell>{a.createdAt ? new Date(a.createdAt).toLocaleString() : '—'}</TableCell>
                <TableCell>{a.action}</TableCell>
                <TableCell>{a.targetType} {a.targetId}</TableCell>
                <TableCell>{a.metadata}</TableCell>
              </TableRow>
            ))}
            {(data?.recentActivity || []).length === 0 && (
              <TableRow><TableCell colSpan={4}>Nothing recorded yet.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast('')} message={toast} />
    </Container>
  );
};

export default TenantDetail;
